import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Loader2, Minus, Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@shared/utils/cn';
import { useTenant } from '@core/tenant';
import type { Reservation } from '../hooks/useUserReservations';
import { userReservationsKeys } from '../hooks/useUserReservations';
import {
  checkModifyTimePolicy,
  fetchAvailableSizesForEdition,
  submitSizeChange,
} from '../api/reservationApi';
import type { PartySizeInfo } from '../api/reservationApi';

type Step = 'loading' | 'choose' | 'confirm';

interface ChangeSizeViewProps {
  reservation: Reservation;
  onClose: () => void;
  onSuccess: () => void;
}

const ChangeSizeView = ({ reservation, onClose, onSuccess }: ChangeSizeViewProps) => {
  const { t } = useTranslation('profile');
  const { subdomainPrefix, restaurant } = useTenant();
  const queryClient = useQueryClient();

  const minSize = typeof restaurant?.minPartySizeAllowed === 'number'
    ? restaurant.minPartySizeAllowed
    : 1;

  const [step, setStep] = useState<Step>('loading');
  const [sizeInfo, setSizeInfo] = useState<PartySizeInfo[]>([]);
  const [selectedSize, setSelectedSize] = useState(reservation.partySize);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const maxSize = sizeInfo.length;
  const currentIndex = selectedSize - 1;
  const isAvailable = sizeInfo[currentIndex]?.available ?? false;
  const isSameSize = selectedSize === reservation.partySize;

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const policy = await checkModifyTimePolicy(reservation.id);
        if (cancelled) return;

        if (!policy.canModify) {
          setError(t('MODIFY_RESERVATION_STRING_12_POLICY'));
          setStep('choose');
          return;
        }

        if (!subdomainPrefix) {
          setError(t('MODIFY_RESERVATION_STRING_12_ERROR'));
          setStep('choose');
          return;
        }

        const sizes = await fetchAvailableSizesForEdition(
          subdomainPrefix,
          reservation.id,
        );
        if (cancelled) return;

        if (sizes.length === 0) {
          setError(t('MODIFY_RESERVATION_STRING_12_NO_SIZES'));
          setStep('choose');
          return;
        }

        setSizeInfo(sizes);
        setStep('choose');
      } catch {
        if (!cancelled) {
          setError(t('MODIFY_RESERVATION_STRING_12_ERROR'));
          setStep('choose');
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, [reservation.id, subdomainPrefix, t]);

  const handleConfirmStep = () => {
    setStep('confirm');
  };

  const handleConfirm = async () => {
    const info = sizeInfo[selectedSize - 1];
    if (!info) return;
    setSubmitting(true);

    try {
      await submitSizeChange(reservation.id, selectedSize, info.areaId);

      if (restaurant?.id) {
        await queryClient.invalidateQueries({
          queryKey: userReservationsKeys.byRestaurant(restaurant.id),
        });
      }

      onSuccess();
    } catch {
      setSubmitting(false);
      setError(t('MODIFY_RESERVATION_STRING_12_ERROR'));
      setStep('choose');
    }
  };

  const handleBack = () => {
    if (step === 'confirm') {
      setStep('choose');
    } else {
      onClose();
    }
  };

  if (step === 'loading') {
    return (
      <div className="w-full flex flex-col items-center py-12">
        <Loader2 size={24} className="animate-spin text-brand" />
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="w-full flex flex-col items-center">
        <h2 className="text-3xl font-display mb-8">{t('MODIFY_RESERVATION_STRING_12_CONFIRM_TITLE')}</h2>

        <div className="flex items-center gap-4 mb-6">
          <div className="text-center">
            <span className="block text-sm text-foreground/50 mb-1">{t('MODIFY_RESERVATION_STRING_12_CURRENT')}</span>
            <span className="text-lg font-semibold text-brand-dark">
              {t('PROFILE_PERSON', { count: reservation.partySize })}
            </span>
          </div>
          <ArrowRight size={20} className="text-foreground/40 mt-4" />
          <div className="text-center">
            <span className="block text-sm text-foreground/50 mb-1">&nbsp;</span>
            <span className="text-lg font-semibold text-brand-dark">
              {t('PROFILE_PERSON', { count: selectedSize })}
            </span>
          </div>
        </div>

        <p className="text-sm text-foreground/70 text-center mb-8 max-w-xs">
          {t('MODIFY_RESERVATION_STRING_12_CONFIRM_MSG')}
        </p>

        <div className="w-full max-w-xs space-y-3">
          <button
            className="w-full py-3 rounded-full bg-brand-dark text-brand-foreground font-medium text-sm tracking-wide hover:bg-brand transition-colors disabled:opacity-50"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? (
              <Loader2 size={18} className="animate-spin mx-auto" />
            ) : (
              t('MODIFY_RESERVATION_STRING_12_CONFIRM')
            )}
          </button>
          <button
            className="w-full py-3 rounded-full border border-border text-brand-dark font-medium text-sm tracking-wide hover:bg-foreground/5 transition-colors"
            onClick={handleBack}
            disabled={submitting}
          >
            {t('MODIFY_RESERVATION_STRING_12_BACK')}
          </button>
        </div>
      </div>
    );
  }

  // Quick-select buttons: minSize through minSize+3
  const quickSizes = Array.from({ length: 4 }, (_, i) => minSize + i).filter(
    (s) => s <= maxSize,
  );
  const showSpinner = maxSize > minSize + 3;

  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-full flex items-center mb-8">
        <button
          className="p-2 -ml-2 text-brand-dark hover:text-brand transition-colors"
          onClick={handleBack}
          aria-label={t('MODIFY_RESERVATION_STRING_12_BACK')}
        >
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-3xl font-display flex-1 text-center pr-8">
          {t('MODIFY_RESERVATION_STRING_12_TITLE')}
        </h2>
      </div>

      {error ? (
        <p className="text-red-500 text-sm text-center mb-6">{error}</p>
      ) : null}

      {maxSize > 0 && (
        <>
          {/* Quick-select buttons */}
          <div className="flex gap-3 mb-6">
            {quickSizes.map((size) => {
              const isCurrent = size === reservation.partySize;
              const isSelected = size === selectedSize;

              return (
                <button
                  key={size}
                  className={cn(
                    'w-14 h-14 rounded-full text-lg font-semibold transition-colors',
                    isSelected && !isCurrent &&
                      'bg-brand-dark text-brand-foreground',
                    isCurrent && isSelected &&
                      'bg-brand-dark/10 text-brand-dark border border-brand-dark/30',
                    isCurrent && !isSelected &&
                      'bg-brand-dark/10 text-brand-dark border border-brand-dark/30',
                    !isSelected && !isCurrent &&
                      'bg-foreground/10 text-foreground/70 hover:bg-foreground/20',
                  )}
                  onClick={() => setSelectedSize(size)}
                >
                  {size}
                </button>
              );
            })}
          </div>

          {/* Spinner for larger sizes */}
          {showSpinner && (() => {
            const spinnerValue = selectedSize > minSize + 3 ? selectedSize : minSize + 4;
            const isSpinnerActive = selectedSize > minSize + 3;

            return (
              <div className="flex items-center gap-4 mb-6">
                <button
                  className="w-10 h-10 rounded-full bg-foreground/10 flex items-center justify-center text-brand-dark hover:bg-foreground/20 transition-colors disabled:opacity-30"
                  onClick={() => setSelectedSize((s) => Math.max(minSize + 4, s - 1))}
                  disabled={!isSpinnerActive || selectedSize <= minSize + 4}
                  aria-label="-"
                >
                  <Minus size={18} />
                </button>
                <button
                  className={cn(
                    'text-2xl font-semibold w-12 text-center rounded-lg py-1 transition-colors',
                    isSpinnerActive
                      ? 'text-brand-dark'
                      : 'text-foreground/40',
                  )}
                  onClick={() => setSelectedSize(minSize + 4)}
                >
                  {spinnerValue}
                </button>
                <button
                  className="w-10 h-10 rounded-full bg-foreground/10 flex items-center justify-center text-brand-dark hover:bg-foreground/20 transition-colors disabled:opacity-30"
                  onClick={() => {
                    if (!isSpinnerActive) {
                      setSelectedSize(minSize + 4);
                    } else {
                      setSelectedSize((s) => Math.min(maxSize, s + 1));
                    }
                  }}
                  disabled={isSpinnerActive && selectedSize >= maxSize}
                  aria-label="+"
                >
                  <Plus size={18} />
                </button>
              </div>
            );
          })()}

          {/* Availability status */}
          {!isSameSize && (
            <p className={cn(
              'text-sm font-medium mb-6',
              isAvailable ? 'text-green-600' : 'text-red-500',
            )}>
              {isAvailable
                ? t('MODIFY_RESERVATION_STRING_12_AVAILABLE')
                : t('MODIFY_RESERVATION_STRING_12_UNAVAILABLE')}
            </p>
          )}

          {/* Confirm button */}
          <div className="w-full max-w-xs">
            <button
              className="w-full py-3 rounded-full bg-brand-dark text-brand-foreground font-medium text-sm tracking-wide hover:bg-brand transition-colors disabled:opacity-50"
              onClick={handleConfirmStep}
              disabled={isSameSize || !isAvailable}
            >
              {t('MODIFY_RESERVATION_STRING_12_CONFIRM')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ChangeSizeView;
